<script setup>
// GK-HUB: Agent deactivation — toggle active/inactive instead of permanent delete
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const currentAgent = ref({});

// GK-HUB: Active/Inactive tabs
const showInactive = ref(false);

const agentList = computed(() => getters['agents/getAgents'].value);
const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

// GK-HUB: Separate active/inactive lists
const activeAgents = computed(() =>
  (agentList.value || []).filter(a => !a.deactivated_at)
);
const inactiveAgents = computed(() =>
  (agentList.value || []).filter(a => a.deactivated_at)
);
const displayedAgents = computed(() =>
  showInactive.value ? inactiveAgents.value : activeAgents.value
);
// GK-HUB: end

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

// GK-HUB: Can toggle status (not for self, not for last admin)
const showToggleAction = agent => {
  if (currentUserId.value === agent.id) return false;
  if (
    agent.role === 'administrator' &&
    !agent.deactivated_at &&
    verifiedAdministrators.value.length <= 1
  )
    return false;
  return true;
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

// GK-HUB: Toggle agent active/inactive
const toggleAgentStatus = async agent => {
  loading.value[agent.id] = true;
  try {
    await store.dispatch('agents/delete', agent.id);
    const msg = agent.deactivated_at
      ? t('AGENT_MGMT.ACTIVATE.API.SUCCESS_MESSAGE')
      : t('AGENT_MGMT.DEACTIVATE.API.SUCCESS_MESSAGE');
    useAlert(msg);
  } catch (error) {
    useAlert(t('AGENT_MGMT.DEACTIVATE.API.ERROR_MESSAGE'));
  }
  loading.value[agent.id] = false;
};
// GK-HUB: end toggle
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        feature-name="agents"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('AGENT_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <!-- GK-HUB: Active/Inactive tabs -->
      <!-- eslint-disable vue/no-bare-strings-in-template -->
      <div class="flex items-center gap-3 mb-4">
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          :class="
            !showInactive
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="showInactive = false"
        >
          {{ $t('AGENT_MGMT.TABS.ACTIVE') }}
          <span class="ml-1 text-xs opacity-70"
            >({{ activeAgents.length }})</span
          >
        </button>
        <button
          class="px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          :class="
            showInactive
              ? 'bg-n-brand text-white'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="showInactive = true"
        >
          {{ $t('AGENT_MGMT.TABS.INACTIVE') }}
          <span class="ml-1 text-xs opacity-70"
            >({{ inactiveAgents.length }})</span
          >
        </button>
      </div>
      <!-- eslint-enable vue/no-bare-strings-in-template -->

      <p v-if="!displayedAgents.length" class="text-sm text-n-slate-9 py-4">
        {{
          showInactive
            ? $t('AGENT_MGMT.TABS.NO_INACTIVE')
            : $t('AGENT_MGMT.LIST.404')
        }}
      </p>

      <table v-else class="divide-y divide-n-weak">
        <tbody class="divide-y divide-n-weak text-n-slate-11">
          <tr
            v-for="agent in displayedAgents"
            :key="agent.email"
            :class="{ 'opacity-50': agent.deactivated_at }"
          >
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-row items-center gap-4">
                <Avatar
                  :src="agent.thumbnail"
                  :name="agent.name"
                  :status="
                    agent.deactivated_at ? 'offline' : agent.availability_status
                  "
                  :size="40"
                  hide-offline-status
                  rounded-full
                />
                <div>
                  <span class="block font-medium capitalize">
                    {{ agent.name }}
                    <!-- GK-HUB: inactive badge -->
                    <span
                      v-if="agent.deactivated_at"
                      class="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-ruby-500/10 text-ruby-500 font-normal"
                    >
                      {{ $t('AGENT_MGMT.STATUS.INACTIVE') }}
                    </span>
                  </span>
                  <span>{{ agent.email }}</span>
                </div>
              </div>
            </td>

            <td class="relative py-4 ltr:pr-4 rtl:pl-4">
              <span
                class="block font-medium w-fit"
                :class="{
                  'hover:text-gray-900 group cursor-pointer':
                    agent.custom_role_id,
                }"
              >
                {{ getAgentRoleName(agent) }}

                <div
                  class="absolute left-0 z-10 hidden max-w-[300px] w-auto bg-white rounded-xl border border-n-weak shadow-lg top-14 md:top-12 dark:bg-n-solid-2"
                  :class="{ 'group-hover:block': agent.custom_role_id }"
                >
                  <div class="flex flex-col gap-1 p-4">
                    <span class="font-semibold">
                      {{ $t('AGENT_MGMT.LIST.AVAILABLE_CUSTOM_ROLE') }}
                    </span>
                    <ul class="pl-4 mb-0 list-disc">
                      <li
                        v-for="permission in getAgentRolePermissions(agent)"
                        :key="permission"
                        class="font-normal"
                      >
                        {{
                          $t(
                            `CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`
                          )
                        }}
                      </li>
                    </ul>
                  </div>
                </div>
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span v-if="agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFIED') }}
              </span>
              <span v-if="!agent.confirmed">
                {{ $t('AGENT_MGMT.LIST.VERIFICATION_PENDING') }}
              </span>
            </td>
            <td class="py-4">
              <div class="flex justify-end gap-1">
                <Button
                  v-if="showEditAction(agent)"
                  v-tooltip.top="$t('AGENT_MGMT.EDIT.BUTTON_TEXT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  @click="openEditPopup(agent)"
                />
                <!-- GK-HUB: Toggle activate/deactivate instead of delete -->
                <Button
                  v-if="showToggleAction(agent)"
                  v-tooltip.top="
                    agent.deactivated_at
                      ? $t('AGENT_MGMT.ACTIVATE.BUTTON_TEXT')
                      : $t('AGENT_MGMT.DEACTIVATE.BUTTON_TEXT')
                  "
                  :icon="
                    agent.deactivated_at
                      ? 'i-lucide-power'
                      : 'i-lucide-power-off'
                  "
                  xs
                  faded
                  :class="
                    agent.deactivated_at
                      ? 'text-green-600 hover:bg-green-500/10'
                      : 'text-ruby-500 hover:bg-ruby-500/10'
                  "
                  :is-loading="loading[agent.id]"
                  @click="toggleAgentStatus(agent)"
                />
                <!-- GK-HUB: end toggle -->
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :custom-role-id="currentAgent.custom_role_id"
        :inbox-ids="currentAgent.inbox_ids || []"
        :team-ids="currentAgent.team_ids || []"
        @close="hideEditPopup"
      />
    </woot-modal>
  </SettingsLayout>
</template>
